import React from 'react';

// ─── Interfaces ─────────────────────────────────────────────────────────────

export interface BlogPostSchemaProps {
  headline: string;
  description?: string;
  datePublished: string;
  dateModified: string;
  authorName: string;
  authorUrl?: string;
  image: string;
  url: string;
  keywords?: string[];
  articleSection?: string;
}

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
}

export interface BlogSchemaProps {
  name: string;
  description: string;
  url: string;
  posts: Array<{
    headline: string;
    url: string;
    datePublished: string;
    author: string;
  }>;
}

export interface VideoSchemaProps {
  name: string;
  description?: string;
  uploadDate: string;
  thumbnailUrl: string;
  embedUrl: string;
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQSchemaProps {
  items: FAQItem[];
}

export interface HowToStep {
  name: string;
  text: string;
  url?: string;
  image?: string;
}

export interface HowToSchemaProps {
  name: string;
  description: string;
  steps: HowToStep[];
  totalTime?: string; // ISO 8601 duration format (e.g., "PT30M")
}

// ─── Components ─────────────────────────────────────────────────────────────

export const BlogPostSchema: React.FC<BlogPostSchemaProps> = ({
  headline,
  description,
  datePublished,
  dateModified,
  authorName,
  authorUrl,
  image,
  url,
  keywords,
  articleSection,
}) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    headline,
    description,
    image,
    datePublished,
    dateModified,
    author: {
      '@type': 'Person',
      name: authorName,
      url: authorUrl,
    },
    articleSection,
    keywords: keywords?.join(', '),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export const BreadcrumbSchema: React.FC<BreadcrumbSchemaProps> = ({ items }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export const BlogSchema: React.FC<BlogSchemaProps> = ({ name, description, url, posts }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name,
    description,
    url,
    blogPost: posts.map(post => ({
      '@type': 'BlogPosting',
      headline: post.headline,
      url: post.url,
      datePublished: post.datePublished,
      author: {
        '@type': 'Person',
        name: post.author,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export const VideoSchema: React.FC<VideoSchemaProps> = ({
  name,
  description,
  uploadDate,
  thumbnailUrl,
  embedUrl,
}) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name,
    description,
    uploadDate,
    thumbnailUrl,
    embedUrl,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export const FAQSchema: React.FC<FAQSchemaProps> = ({ items }) => {
  if (!items || items.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export const HowToSchema: React.FC<HowToSchemaProps> = ({ name, description, steps, totalTime }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    totalTime,
    step: steps.map(step => ({
      '@type': 'HowToStep',
      name: step.name,
      text: step.text,
      url: step.url,
      image: step.image,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export interface OrganizationSchemaProps {
  name: string;
  url: string;
  logo: string;
  description?: string;
  sameAs?: string[];
}

export const OrganizationSchema: React.FC<OrganizationSchemaProps> = ({
  name,
  url,
  logo,
  description,
  sameAs,
}) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name,
    url,
    logo,
    description,
    sameAs,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};